import _ from "lodash";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue, useSetRecoilState } from "recoil";

import {
  apiDataFetchingErrorsAtom,
  controlsSF,
  createErrorResult,
  errorsCrudContentAtomFamily,
  filterOnlyDirty,
  FutureBookingsConfirmOutcome,
  FutureBookingsImpactApi,
  getSiblingToValidationFields,
  IExtendableDto,
  IShiftStationOptionsChange,
  Log,
  SHIFT_STATION_OPTION_FIELDS,
  SimpleResult,
} from "@ryde/shared";

import { useCrudEditContext } from "../../contexts";
import {
  buildShiftStationOptionsChange,
  resolveShouldUpdateFutureBookingsStations,
  useCrudSave,
  useFutureBookingsConfirm,
  useShiftTimeChangeConfirm,
} from "../dynamic-forms";

/**
 * Dedicated shift save flow (mirrors useCourseEdit / useStationRouteEdit).
 *
 * Validates the form FIRST, then asks the admin whether future "New" bookings should follow the
 * change — for shift hours (retiming) and, since RYDE-7929, for the shift's default station
 * selection. Keeping this out of the generic saveCrudEntity avoids prompting the admin before the
 * form is known valid, and keeps the generic save free of entity-specific async-dialog logic.
 *
 * The two questions are INDEPENDENT and travel on separate flags, so answering "no" to one can never
 * be overridden by answering "yes" to the other in the same save.
 */
export const useShiftEdit = <T extends IExtendableDto>() => {
  const {
    entity,
    data: { isEdit, save, dto: contextDto },
  } = useCrudEditContext<T>();
  const { saveCrudEntity } = useCrudSave<T>();
  const { confirmUpdateFutureBookings } = useShiftTimeChangeConfirm();
  const { confirmUpdateFutureBookingsStations } = useFutureBookingsConfirm();
  const { t } = useTranslation();
  const controls = useRecoilValue(controlsSF(entity));
  const {
    getValues,
    trigger,
    formState: { dirtyFields },
  } = useFormContext();
  const setCrudServerErrors = useSetRecoilState(
    errorsCrudContentAtomFamily(entity),
  );
  const setApiServerErrors = useSetRecoilState(apiDataFetchingErrorsAtom);

  const saveShift = useEvent(async (dto?: T): Promise<SimpleResult<T>> => {
    setCrudServerErrors(undefined);
    setApiServerErrors(undefined);

    // Validate before prompting so the admin is never asked to confirm and then blocked by a form error.
    const isValidForm = await trigger();
    if (!isValidForm) {
      Log.warnAndToast(t("errors.formIsInvalidCheckValidationErrors"));
      return createErrorResult<T>("errors.formIsInvalidCheckValidationErrors");
    }

    const formData = getValues() as Partial<T> & IShiftStationOptionsChange;
    const shiftId = contextDto?.id ?? dto?.id;
    const hasStationOptionChange =
      isEdit &&
      SHIFT_STATION_OPTION_FIELDS.some(field =>
        Boolean(_.get(dirtyFields, field)),
      );

    let outcome = FutureBookingsConfirmOutcome.NotApplicable;

    if (hasStationOptionChange && shiftId) {
      const shiftStationOptionsChange =
        buildShiftStationOptionsChange(formData);

      outcome = await confirmUpdateFutureBookingsStations({
        isEdit: Boolean(isEdit),
        dirtyFields,
        triggerFields: SHIFT_STATION_OPTION_FIELDS,
        getImpactCount: () =>
          FutureBookingsImpactApi.forShift(shiftId, shiftStationOptionsChange),
      });
    }

    // Never guess when the impact is unknown: station propagation defaults to ON server-side, so saving
    // without an explicit answer would cascade a mass change nobody approved. Abort and let them retry.
    // The API layer already toasted the real server error, so don't stack a generic one on top.
    if (outcome === FutureBookingsConfirmOutcome.PreviewFailed) {
      return createErrorResult<T>("errors.failedToCheckFutureBookingsImpact");
    }

    const shouldUpdateFutureBookingsStations =
      resolveShouldUpdateFutureBookingsStations(outcome);

    // Asked independently: the retiming question has its own flag, so the two answers never collide.
    const shouldRetimeFutureBookings = await confirmUpdateFutureBookings({
      isEdit: Boolean(isEdit),
      dirtyFields,
    });

    // Nothing to propagate and nothing to suppress (create/duplicate, or no trigger field changed):
    // reuse the generic save path, which also handles duplicate-mode payloads.
    if (
      (shouldUpdateFutureBookingsStations === undefined &&
        !shouldRetimeFutureBookings) ||
      save === undefined
    ) {
      return saveCrudEntity(dto);
    }

    const siblingToValidationFields = getSiblingToValidationFields({
      controls,
      formFields: Object.keys(formData),
    });
    const formDataCleaned = filterOnlyDirty({
      formFields: formData,
      dirtyFields,
      siblingToValidationFields,
    });

    // The stations answer is sent explicitly even when false: station propagation defaults to ON
    // server-side, so declining the popup has to be stated, not implied by omission. Retiming is the
    // opposite — it is opt-in, so it is only sent when approved.
    const shiftForApiSend: Partial<T> & {
      shouldUpdateFutureBookings?: boolean;
      shouldUpdateFutureBookingsStations?: boolean;
    } = {
      ...formDataCleaned,
      ...(shouldRetimeFutureBookings
        ? { shouldUpdateFutureBookings: true }
        : {}),
      ...(shouldUpdateFutureBookingsStations === undefined
        ? {}
        : { shouldUpdateFutureBookingsStations }),
    };

    return save(shiftForApiSend);
  });

  return { saveShift };
};
